import type { TelemetryEventPayload } from "@/lib/analytics/events";

export type AnalyticsSurface = "client" | "coach" | "admin" | "public";
export type TopLevelTab = TelemetryEventPayload<"top_level_tab_opened">["tab"];
export type TelemetrySubtab = TelemetryEventPayload<"subtab_opened">["subtab"];

type SearchParamLike =
  | string
  | URLSearchParams
  | {
      get(name: string): string | null;
    }
  | null
  | undefined;

type TelemetryLocationState = {
  galleryTab?: string | null;
};

const CLIENT_NUTRITION_SUBTABS = ["log", "history", "meals"] as const;
const CLIENT_GALLERY_SUBTABS = ["uploads", "comparisons", "checkins"] as const;

export function getAnalyticsSurfaceFromPath(pathname: string): AnalyticsSurface {
  if (pathname === "/admin" || pathname.startsWith("/admin/")) return "admin";
  if (pathname === "/client" || pathname.startsWith("/client/")) return "client";
  if (pathname === "/coach" || pathname.startsWith("/coach/")) return "coach";
  return "public";
}

export function getTopLevelTabFromPath(pathname: string): TopLevelTab | null {
  if (pathname === "/admin" || pathname.startsWith("/admin/")) return "dashboard";

  if (pathname === "/client" || pathname.startsWith("/client/")) {
    if (pathname === "/client") return "home";
    if (/^\/client\/(program|workout|history|exercises)(\/|$)/.test(pathname)) return "train";
    if (/^\/client\/(weight|measurements)(\/|$)/.test(pathname)) return "track";
    if (/^\/client\/nutrition(\/|$)/.test(pathname)) return "nutrition";
    if (/^\/client\/(gallery|chat)(\/|$)/.test(pathname)) return "upload";
    return null;
  }

  if (pathname === "/coach" || pathname.startsWith("/coach/")) {
    if (/^\/coach\/clients(\/|$)/.test(pathname)) return "clients";
    if (/^\/coach\/programs(-v2)?(\/|$)/.test(pathname)) return "programs";
    if (/^\/coach\/exercises(\/|$)/.test(pathname)) return "exercises";
    if (/^\/coach(\/dashboard-v2)?(\/|$)/.test(pathname)) return "dashboard";
    return null;
  }

  return null;
}

function getSearchParam(searchParams: SearchParamLike, key: string): string | null {
  if (!searchParams) return null;
  if (typeof searchParams === "string") {
    return new URLSearchParams(searchParams.startsWith("?") ? searchParams.slice(1) : searchParams).get(key);
  }
  return searchParams.get(key);
}

function normalizeSubtab(value: string | null | undefined): TelemetrySubtab | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, 64);
}

function normalizeAllowedSubtab<TValue extends string>(
  value: string | null | undefined,
  allowed: readonly TValue[]
): TValue | null {
  const normalized = normalizeSubtab(value);
  if (!normalized) return null;
  const match = allowed.find((entry) => entry === normalized);
  return match ?? null;
}

export function getGalleryTelemetrySubtab(value: string | null | undefined): TelemetrySubtab {
  return normalizeAllowedSubtab(value, CLIENT_GALLERY_SUBTABS) ?? "uploads";
}

function getNutritionTelemetrySubtab(searchParams?: SearchParamLike): TelemetrySubtab {
  return normalizeAllowedSubtab(getSearchParam(searchParams, "tab"), CLIENT_NUTRITION_SUBTABS) ?? "log";
}

function getCoachProgramsV2TelemetrySubtab(searchParams?: SearchParamLike): TelemetrySubtab {
  const clientProgramId = getSearchParam(searchParams, "clientProgramId");
  if (clientProgramId) return "client_editor";
  const programId = getSearchParam(searchParams, "programId");
  if (programId) return "editor";
  return "library";
}

export function getTelemetrySubtabFromLocation(
  pathname: string,
  searchParams?: SearchParamLike,
  state?: TelemetryLocationState
): { tab: TopLevelTab; subtab: TelemetrySubtab } | null {
  if (pathname === "/admin" || pathname.startsWith("/admin/")) {
    return { tab: "dashboard", subtab: "overview" };
  }

  if (pathname === "/client") {
    return { tab: "home", subtab: "dashboard" };
  }
  if (/^\/client\/program(\/|$)/.test(pathname)) {
    return { tab: "train", subtab: "program" };
  }
  if (/^\/client\/history(\/|$)/.test(pathname)) {
    return { tab: "train", subtab: "history" };
  }
  if (/^\/client\/exercises\/[^/]+(\/|$)/.test(pathname)) {
    return { tab: "train", subtab: "exercise_detail" };
  }
  if (/^\/client\/exercises(\/|$)/.test(pathname)) {
    return { tab: "train", subtab: "exercises" };
  }
  if (/^\/client\/workout(\/|$)/.test(pathname)) {
    return { tab: "train", subtab: "workout" };
  }
  if (/^\/client\/weight(\/|$)/.test(pathname)) {
    return { tab: "track", subtab: "weight" };
  }
  if (/^\/client\/measurements(\/|$)/.test(pathname)) {
    return { tab: "track", subtab: "measurements" };
  }
  if (/^\/client\/nutrition(\/|$)/.test(pathname)) {
    return {
      tab: "nutrition",
      subtab: getNutritionTelemetrySubtab(searchParams),
    };
  }
  if (/^\/client\/gallery(\/|$)/.test(pathname)) {
    if (state?.galleryTab == null) return null;
    return {
      tab: "upload",
      subtab: getGalleryTelemetrySubtab(state.galleryTab),
    };
  }
  if (/^\/client\/chat(\/|$)/.test(pathname)) {
    return { tab: "upload", subtab: "chat" };
  }
  if (pathname === "/coach" || /^\/coach\/dashboard-v2(\/|$)/.test(pathname)) {
    return { tab: "dashboard", subtab: "overview" };
  }
  if (/^\/coach\/clients\/[^/]+\/dashboard-v2(\/|$)/.test(pathname)) {
    return { tab: "clients", subtab: "dashboard" };
  }
  if (/^\/coach\/clients\/[^/]+\/chat(\/|$)/.test(pathname)) {
    return { tab: "clients", subtab: "chat" };
  }
  if (/^\/coach\/clients\/[^/]+\/gallery(\/|$)/.test(pathname)) {
    return { tab: "clients", subtab: "gallery" };
  }
  if (/^\/coach\/clients\/[^/]+\/view(\/|$)/.test(pathname)) {
    return { tab: "clients", subtab: "view" };
  }
  if (/^\/coach\/clients(\/|$)/.test(pathname)) {
    return { tab: "clients", subtab: "list" };
  }
  if (/^\/coach\/programs-v2(\/|$)/.test(pathname)) {
    return { tab: "programs", subtab: getCoachProgramsV2TelemetrySubtab(searchParams) };
  }
  if (/^\/coach\/programs\/new(\/|$)/.test(pathname)) {
    return { tab: "programs", subtab: "new" };
  }
  if (/^\/coach\/programs\/[^/]+(\/|$)/.test(pathname)) {
    return { tab: "programs", subtab: "editor" };
  }
  if (/^\/coach\/exercises\/new(\/|$)/.test(pathname)) {
    return { tab: "exercises", subtab: "new" };
  }
  if (/^\/coach\/exercises\/[^/]+(\/|$)/.test(pathname)) {
    return { tab: "exercises", subtab: "detail" };
  }
  if (/^\/coach\/exercises(\/|$)/.test(pathname)) {
    return { tab: "exercises", subtab: "library" };
  }

  return null;
}
